import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Switch } from 'antd';
import Plot from '../../../../node_modules/react-plotly.js/react-plotly';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useScreenHeight, useScreenWidth } from '../../../hooks/useScreen';

const BiplotChart = ({ loadingMatrices }) => {
  const chartRef = useRef(null);
  const [selectedComponents, setSelectedComponents] = useState([0, 1, 2]);
  const screenHeight = useScreenHeight();
  const screenWidth = useScreenWidth();

  const handleComponentToggle = (index, checked) => {
    let updatedComponents;
    if (checked) {
      if (selectedComponents.length >= 3) {
        updatedComponents = [...selectedComponents.slice(1), index];
      } else {
        updatedComponents = [...selectedComponents, index];
      }
    } else {
      updatedComponents = selectedComponents.filter(component => component !== index);
    }
    setSelectedComponents(updatedComponents);
  };

  const handleReorientChart = () => {
    if (chartRef.current) {
      chartRef.current.layout.scene.camera.eye = {
        x: 1.25,
        y: 1.25,
        z: 1.25,
      };
    }
  };

  // Dynamically calculate chart dimensions based on screen width and height with constraints
  const chartDimensions = {
    width: Math.min(screenWidth * 0.85, 1200),
    height: Math.min(screenHeight * 0.75, 700),
  };

  const variables = Object.keys(loadingMatrices[0].data);
  const loadings = variables.map(variable => ({
    variable,
    x: loadingMatrices[selectedComponents[0]]?.data[variable] ?? 0,
    y: loadingMatrices[selectedComponents[1]]?.data[variable] ?? 0,
    z: loadingMatrices[selectedComponents[2]]?.data[variable] ?? 0,
  }));

  let traces;
  let layout;

  if (selectedComponents.length > 2) {
    // 3D Scatter Plot
    traces = loadings.map((loading, index) => ({
      x: [0, loading.x],
      y: [0, loading.y],
      z: [0, loading.z],
      mode: 'lines+markers+text',
      type: 'scatter3d',
      name: loading.variable,
      line: {
        color: `hsl(${(index * 360) / loadings.length}, 70%, 50%)`,
        width: 6,
      },
      marker: {
        size: 10,
        color: `hsl(${(index * 360) / loadings.length}, 70%, 50%)`,
        symbol: 'diamond',
      },
      text: `${loading.variable}`,
      textposition: 'top right',
      hoverinfo: 'text',
      hovertext: `${loading.variable}<br>PC1: ${loading.x.toFixed(3)}<br>PC2: ${loading.y.toFixed(3)}<br>PC3: ${loading.z.toFixed(3)}`,
    }));

    layout = {
      title: {
        text: 'Component Loading Directions',
        font: {
          size: 13,
          fontFamily: 'var(--font-main)',
          fontWeight: '500'
        },
      },
      margin: {
        l: 0,
        r: 0,
        b: 0,
        t: 30,
      },
      scene: {
        xaxis: {
          title: `PC ${selectedComponents[0] + 1}`,
          range: [-1.2, 1.2],
        },
        yaxis: {
          title: `PC ${selectedComponents[1] + 1}`,
          range: [-1.2, 1.2],
        },
        zaxis: {
          title: `PC ${selectedComponents[2] + 1}`,
          range: [-1.2, 1.2],
        },
        camera: {
          eye: {
            x: 1.25,
            y: 1.25,
            z: 1.25,
          },
          center: {
            x: 0,
            y: 0,
            z: 0,
          },
          up: {
            x: 0,
            y: 0,
            z: 1,
          },
        },
      },
      showlegend: false,
      hovermode: 'closest',
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: '#fff',
    };
  } else {
    // 2D Scatter Plot
    traces = loadings.map((loading, index) => ({
      x: [0, loading.x],
      y: [0, loading.y],
      mode: 'lines+markers+text',
      type: 'scatter',
      name: loading.variable,
      line: {
        color: `hsl(${(index * 360) / loadings.length}, 70%, 50%)`,
        width: 6,
      },
      marker: {
        size: 10,
        color: `hsl(${(index * 360) / loadings.length}, 70%, 50%)`,
        symbol: 'diamond',
      },
      text: `${loading.variable}`,
      textposition: 'top right',
      hoverinfo: 'text',
      hovertext: `${loading.variable}<br>PC1: ${loading.x.toFixed(3)}<br>PC2: ${loading.y.toFixed(3)}`,
    }));

    layout = {
      margin: {
        l: 0,
        r: 0,
        b: 0,
        t: 30,
      },
      xaxis: {
        title: `PC ${selectedComponents[0] + 1}`,
        range: [-1.2, 1.2],
      },
      yaxis: {
        title: `PC ${selectedComponents[1] + 1}`,
        range: [-1.2, 1.2],
      },
      showlegend: true,
      hovermode: 'closest',
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: '#fff',
    };
  }

  return (
    <Row align={'top'} wrap={false}>
      <Col flex={'0 0 200px'}>
        <IconButton color="primary" onClick={handleReorientChart} style={{ marginTop: '20px' }}>
          <AutorenewIcon />
        </IconButton>
        {loadingMatrices.length > 3 && (
          <div style={{ marginTop: '20px' }}>
            {loadingMatrices.map((_, index) => (
              <div key={index} style={{ marginBottom: '10px' }}>
                <Switch
                  checked={selectedComponents.includes(index)}
                  onChange={(checked) => handleComponentToggle(index, checked)}
                  checkedChildren={`PC ${index + 1}`}
                  unCheckedChildren={`PC ${index + 1}`}
                />
              </div>
            ))}
          </div>
        )}
      </Col>
      <Col flex={'1'}>
        <div
          ref={chartRef}
          onWheel={(e) => {
            if (!chartRef.current || !chartRef.current.contains(e.target)) {
              e.stopPropagation();
            }
          }}
          style={{ flex: 1 }}
        >
          <Plot
            data={traces}
            layout={{ ...layout, width: chartDimensions.width, height: chartDimensions.height }}
            config={{
              responsive: true,
              displayModeBar: false,
              modeBarButtonsToRemove: ['lasso2d', 'select2d'],
              displaylogo: false,
              scrollZoom: true,
            }}
          />
        </div>
      </Col>
    </Row>
  );
};

export default BiplotChart;
